import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Boxes,
  CheckCircle,
  Database,
  ExternalLink,
  Gauge,
  Layers,
  Settings,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BrandIcons } from '@/components/ui/brand-icons';
import MemoryTypesBadges from '@/components/ui/MemoryTypesBadges';

const DifyIcon = BrandIcons.dify;

export default function DifyLanding() {
  const { t } = useTranslation('integrations');

  const momentumItems = t('difyLanding.momentum.items', { returnObjects: true }) as string[];
  const setupSteps = t('difyLanding.setup.steps', { returnObjects: true }) as string[];
  const operations = t('difyLanding.operations.items', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;
  const useCases = t('difyLanding.useCases.items', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50">
      <SEOHead
        title={t('difyLanding.seo.title')}
        description={t('difyLanding.seo.description')}
        keywords={t('difyLanding.seo.keywords', { returnObjects: true }) as string[]}
        ogType="article"
        priority="high"
      />

      <nav className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/integrations"
            className="flex items-center text-gray-600 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            {t('difyLanding.nav.back')}
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              {t('difyLanding.nav.badge')}
            </Badge>
            <Button asChild size="sm">
              <Link to="/signup">{t('difyLanding.nav.cta')}</Link>
            </Button>
          </div>
        </div>
      </nav>

      <section className="pb-20 pt-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8 flex justify-center">
              <div className="flex items-center space-x-4 rounded-2xl border bg-white p-6 shadow-lg">
                <DifyIcon className="h-16 w-16" />
                <div className="h-px w-8 bg-gradient-to-r from-indigo-500 to-violet-600" />
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600">
                  <span className="text-xl font-bold text-white">M</span>
                </div>
              </div>
            </div>

            <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-6xl">
              {t('difyLanding.hero.titlePrefix')}{' '}
              <span className="text-indigo-600">{t('difyLanding.hero.titleEmphasis')}</span>
            </h1>

            <p className="mx-auto mb-8 max-w-3xl text-xl text-gray-600">
              {t('difyLanding.hero.subtitle')}
            </p>

            <div className="mx-auto mb-10 max-w-2xl rounded-xl border border-gray-200 bg-white/90 px-4 py-5 shadow-sm">
              <h2 className="mb-3 text-center text-sm font-semibold text-gray-800">
                {t('memoryModalities.sectionTitle')}
              </h2>
              <MemoryTypesBadges integrationId="dify" className="justify-center" />
            </div>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
                <Link to="/signup">
                  {t('difyLanding.hero.primaryCta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-indigo-200 text-indigo-700">
                <a
                  href="https://docs.dify.ai/en/develop-plugin/dev-guides-and-walkthroughs/tool-plugin"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('difyLanding.hero.secondaryCta')}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              {t('difyLanding.momentum.title')}
            </h2>
            <p className="text-lg text-gray-600">{t('difyLanding.momentum.subtitle')}</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {momentumItems.map((item) => (
              <div key={item} className="rounded-lg border bg-slate-50 p-4 text-sm text-slate-700">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              {t('difyLanding.integrationModel.title')}
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-gray-600">
              {t('difyLanding.integrationModel.subtitle')}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-lg border bg-white p-6">
              <div className="mb-4 inline-flex rounded-lg bg-indigo-100 p-2">
                <Database className="h-5 w-5 text-indigo-700" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {t('difyLanding.integrationModel.cards.key.title')}
              </h3>
              <p className="text-sm text-gray-600">
                {t('difyLanding.integrationModel.cards.key.description')}
              </p>
            </div>
            <div className="rounded-lg border bg-white p-6">
              <div className="mb-4 inline-flex rounded-lg bg-violet-100 p-2">
                <Layers className="h-5 w-5 text-violet-700" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {t('difyLanding.integrationModel.cards.api.title')}
              </h3>
              <p className="text-sm text-gray-600">
                {t('difyLanding.integrationModel.cards.api.description')}
              </p>
            </div>
            <div className="rounded-lg border bg-white p-6">
              <div className="mb-4 inline-flex rounded-lg bg-sky-100 p-2">
                <Gauge className="h-5 w-5 text-sky-700" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {t('difyLanding.integrationModel.cards.validation.title')}
              </h3>
              <p className="text-sm text-gray-600">
                {t('difyLanding.integrationModel.cards.validation.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">{t('difyLanding.setup.title')}</h2>
            <p className="text-lg text-gray-600">{t('difyLanding.setup.subtitle')}</p>
          </div>

          <div className="space-y-4">
            {setupSteps.map((step, idx) => (
              <div key={step} className="rounded-lg border bg-white p-5 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
                    {idx + 1}
                  </div>
                  <p className="pt-1 text-sm text-gray-700">{step}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              {t('difyLanding.operations.title')}
            </h2>
            <p className="text-lg text-gray-600">{t('difyLanding.operations.subtitle')}</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {operations.map((operation) => (
              <div key={operation.title} className="rounded-lg border bg-white p-6">
                <div className="mb-3 inline-flex rounded-md bg-indigo-100 p-2">
                  <CheckCircle className="h-4 w-4 text-indigo-700" />
                </div>
                <h3 className="mb-2 text-base font-semibold text-gray-900">{operation.title}</h3>
                <p className="text-sm text-gray-600">{operation.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-xl border border-indigo-200 bg-indigo-50 p-5 text-sm text-indigo-900">
            <p>{t('difyLanding.operations.limits')}</p>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">{t('difyLanding.useCases.title')}</h2>
            <p className="text-lg text-gray-600">{t('difyLanding.useCases.subtitle')}</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {useCases.map((useCase, idx) => {
              const icons = [<Wrench className="h-5 w-5" />, <Settings className="h-5 w-5" />, <Boxes className="h-5 w-5" />];
              return (
                <div key={useCase.title} className="rounded-lg border p-6">
                  <div className="mb-3 inline-flex rounded-md bg-slate-100 p-2 text-slate-700">
                    {icons[idx] ?? <Wrench className="h-5 w-5" />}
                  </div>
                  <h3 className="mb-2 text-base font-semibold text-gray-900">{useCase.title}</h3>
                  <p className="text-sm text-gray-600">{useCase.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-4 text-3xl font-bold">{t('difyLanding.finalCta.title')}</h2>
          <p className="mb-8 text-lg opacity-90">{t('difyLanding.finalCta.subtitle')}</p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" variant="secondary" className="bg-white text-indigo-700 hover:bg-gray-100">
              <Link to="/signup">{t('difyLanding.finalCta.primary')}</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-indigo-700"
            >
              <a
                href="https://github.com/memaraio/memara-dify-plugin"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('difyLanding.finalCta.secondary')}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
